import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API from "../../api/api";
import AdminLayout from "../../components/admin/AdminLayout";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

// Custom Bracket components
import BracketComp from "./BracketComp";

export default function TournamentBracket() {
  const { id: tournamentId } = useParams();

  const [brackets, setBrackets] = useState([]);
  const [selectedBracket, setSelectedBracket] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loadingBrackets, setLoadingBrackets] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [error, setError] = useState(null);

  // -----------------------
  // Load brackets list
  // -----------------------
  const loadBrackets = async () => {
    setLoadingBrackets(true);
    setError(null);
    try {
      const data = await API.get(`/tournaments/${tournamentId}/brackets`);
      setBrackets(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load brackets");
      setBrackets([]);
    } finally {
      setLoadingBrackets(false);
    }
  };

  useEffect(() => {
    if (brackets.length > 0 && selectedBracket === null) {
      setSelectedBracket(brackets[0].bracket_id);
    }
  }, [brackets, selectedBracket]);

  // -----------------------
  // Load matches for selected bracket
  // -----------------------
  const loadBracketDetails = async (bracketId) => {
    if (!bracketId) return;
    setLoadingDetails(true);
    setError(null);
    try {
      const rows = await API.get(`/brackets/${bracketId}`);
      setMatches(rows);
    } catch (err) {
      console.error(err);
      setError("Failed to load bracket details");
      setMatches([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (tournamentId) loadBrackets();
  }, [tournamentId]);

  useEffect(() => {
    if (selectedBracket) loadBracketDetails(selectedBracket);
  }, [selectedBracket]);

  // -----------------------
  // Generate brackets
  // -----------------------
  const handleGenerateBrackets = async () => {
    if (!window.confirm("Are you sure you want to generate brackets?")) return;
    setLoadingGenerate(true);
    setError(null);
    try {
      await API.post(`/tournaments/${tournamentId}/brackets/generate`);
      await loadBrackets();
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to generate brackets");
    } finally {
      setLoadingGenerate(false);
    }
  };

  // -----------------------
  // Update winner
  // -----------------------
  const handleWinnerSelect = async (matchId, fighterId) => {
    try {
      await API.post(`/brackets/${selectedBracket}/update-winner`, {
        match_id: matchId,
        winner_id: fighterId,
      });
      await loadBracketDetails(selectedBracket);
    } catch (err) {
      console.error(err);
      setError("Failed to update winner");
    }
  };

  // -----------------------
  // Add judge
  // -----------------------
  const handleAddJudge = async (matchId, judgeName) => {
    try {
      await API.post(`/brackets/${selectedBracket}/add-judge`, {
        match_id: matchId,
        judge_name: judgeName,
      });
      await loadBracketDetails(selectedBracket);
    } catch (err) {
      console.error(err);
      setError("Failed to add judge");
    }
  };

const handleMoveFighter = async (fromMatchId, toMatchId, fighter) => {
  try {
    await API.post(`/brackets/${selectedBracket}/move-fighter`, {
      from_match_id: fromMatchId,
      to_match_id: toMatchId,
      fighter_id: fighter.id.replace("fighter-", ""), // remove prefix
    });
    await loadBracketDetails(selectedBracket);
  } catch (err) {
    console.error("Failed to move fighter:", err);
    setError("Failed to move fighter");
  }
};

  // -----------------------
  // Transform matches into rounds
  // -----------------------
const buildRounds = () => {
  if (!matches || matches.length === 0) return [];

  // --- 1. Get first round matches ---

  const firstRoundMatches = matches.filter(
    (m) => Number(m.round) === 1
  );

  if (firstRoundMatches.length === 0) return [];

  // --- 2. Calculate total rounds (log2 of fighters) ---
  const fightersCount = firstRoundMatches.length * 2;
  const totalRounds = Math.ceil(Math.log2(fightersCount));

  const rounds = [];

  for (let r = 1; r <= totalRounds; r++) {
    const roundMatches = matches.filter(
      (m) => Number(m.round) === r
    );

    const expectedMatches =
      r === 1
        ? firstRoundMatches.length
        : Math.ceil(firstRoundMatches.length / Math.pow(2, r - 1));

    rounds.push({
      title:
        r === totalRounds
          ? "Final"
          : r === totalRounds - 1
          ? "Semi Final"
          : `Round ${r}`,

      matches:
        roundMatches.length > 0
          ? roundMatches.map((m) => ({
              id: m.match_id,
              fighters: [
                {
                  id: m.fighter1_id ?? `bye-${m.match_id}-1`,
                  name: m.fighter1_name ?? "BYE",
                },
                {
                  id: m.fighter2_id ?? `bye-${m.match_id}-2`,
                  name: m.fighter2_name ?? "BYE",
                },
              ],
              winnerId: m.winner_id ?? null,
              judge: m.judge_name ?? null,
            }))
          : Array.from({ length: expectedMatches }, (_, i) => ({
              id: `placeholder-${r}-${i}`,
              fighters: [
                { id: `tbd-${r}-${i}-1`, name: "TBD" },
                { id: `tbd-${r}-${i}-2`, name: "TBD" },
              ],
              winnerId: null,
              judge: null,
            })),
    });
  }

  return rounds;
};
const rounds = buildRounds();


  // -----------------------
  // Render
  // -----------------------
  return (
    <AdminLayout>
      <div className="p-6 text-white">
        <h1 className="text-3xl font-bold mb-6">Tournament Brackets</h1>

        {error && <div className="text-red-500 mb-4">{error}</div>}

        <div className="mb-4 flex gap-2 items-center">
          {loadingBrackets ? (
            <p>Loading brackets...</p>
          ) : (
            <>
              <select
                value={selectedBracket ?? ""}
                onChange={(e) => setSelectedBracket(Number(e.target.value))}
                className="p-2 bg-gray-800 rounded"
              >
                <option value="" disabled>
                  Select a bracket
                </option>
                {brackets.map((b) => (
                  <option key={b.bracket_id} value={b.bracket_id}>
                    Age {b.age_division_id} � {b.weightclass_id}kg ({b.status})
                  </option>
                ))}
              </select>

              <button
                onClick={handleGenerateBrackets}
                disabled={loadingGenerate}
                className="px-3 py-1 bg-blue-600 rounded"
              >
                {loadingGenerate ? "Generating..." : "Generate Brackets"}
              </button>
            </>
          )}
        </div>

        {loadingDetails ? (
          <p>Loading bracket details...</p>
        ) : rounds.length === 0 ? (
          <p>No matches generated yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <DndProvider backend={HTML5Backend}>
  <BracketComp
    rounds={rounds}
    onWinnerSelect={handleWinnerSelect}
    onAddJudge={handleAddJudge}
    onMoveFighter={handleMoveFighter} // NEW: function to handle drag & drop
  />
</DndProvider>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
